import { useState } from 'react';
import { Button, Card, Modal, Space, Tag, Typography, Checkbox, Tooltip, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons';
import { t } from '../../../i18n/i18n';
// import './TestGroupAdvancedSettings.scss'; // Removed: using utility classes instead
import { PresetEditor } from './PresetEditor';
import ImportAdvancedConfigModal from './ImportAdvancedConfigModal';
import GenericToggle from '../../GenericToggle/GenericToggle';
import DropDownSelect from '../../DropdownSelect/DropdownSelect';
import TestGroupsItem from '../../TestGroupsItem/TestGroupsItem';
import TreeMultiSelect from '../../TreeMultiSelect/TreeMultiSelect';
import useTranslations from '../../../hooks/useTranslations';
import { getTranslatedValue } from '../../../utils/translationHelpers';

const { Title, Text } = Typography;

export const TestGroupAdvancedSettings = ({
  advancedSetting,
  setAdvancedSetting,
  testDifficulty,
  testQuestions,
  withThemes,
  testGroupQuestions,
  readOnly = false,
  // Simple mode props from TestGroupDifficultyConfig
  testGroupQuantityQuestions,
  setTestGroupQuantityQuestions,
  onBlurInputNumber,
  handleSelectDifficulty,
  testGroupDifficult,
  availableThemes,
  handleSelectTheme,
  testGroupTheme,
  testGroupListeningTest,
  handleAudioDifficulty,
  handleCreateQuestionsSet,
  disabledCreateSet,
  setShow,
  setItemDelete,
  handleOrderSetQuestions,
  availableSubskills = [],
  handleSelectSubskills,
  testGroupSubskills = [],
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const { key: langKey } = useTranslations();
  const [isEditMode, setIsEditMode] = useState(false);
  const isSimpleMode = !advancedSetting?.available;
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);

  const handleToggleAdvanced = (checked) => {
    const newSetting = {
      available: checked,
      preSettings: advancedSetting?.preSettings ?? [],
    };
    setAdvancedSetting(newSetting);
  };

  // Calculate available questions count by difficulty/theme/subskills
  const getAvailableQuestionsCount = (difficultyId, theme = null, subSkillIds = []) => {
    if (!testQuestions || testQuestions.length === 0) return 0;

    return testQuestions.filter((q) => {
      const matchDifficulty = q.difficultyId === difficultyId;

      // If subskills are selected, only count questions that have at least one of the selected subskills
      const matchSubskills =
        !subSkillIds ||
        subSkillIds.length === 0 ||
        (q.subSkillIds && q.subSkillIds.some((subskillId) => subSkillIds.includes(subskillId)));

      if (!withThemes) {
        return matchDifficulty && matchSubskills;
      }

      const matchTheme = !theme || q.theme === theme;
      return matchDifficulty && matchTheme && matchSubskills;
    }).length;
  };

  const handleAddPreset = () => {
    // Create a completely empty preset to avoid showing current configuration
    const emptyPreset = {
      id: Date.now(), // Temporal ID for new presets
      displayName: '',
      difficultyConfig: [],
      // Explicitly set to avoid any default values
      isNew: true,
    };
    setEditingPreset(emptyPreset);
    setIsEditMode(false);
    setIsModalVisible(true);
  };

  const handleEditPreset = (preset) => {
    // Make sure we're not marking it as new when editing
    const editingPresetData = { ...preset, isNew: false };
    setEditingPreset(editingPresetData);
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const handleDeletePreset = (presetId) => {
    Modal.confirm({
      title: t('TESTGROUP_ADVANCED_DELETE_PRESET_TITLE'),
      content: t('TESTGROUP_ADVANCED_DELETE_PRESET_CONFIRM'),
      okText: t('DELETE'),
      cancelText: t('CANCEL'),
      onOk: () => {
        const newPreSettings = advancedSetting.preSettings.filter((p) => p.id !== presetId);

        // If this was the last preset, automatically switch back to simple mode
        if (newPreSettings.length === 0) {
          setAdvancedSetting({
            available: false,
            preSettings: [],
          });
          message.info(t('TESTGROUP_ADVANCED_CONFIG_REMOVED'));
        } else {
          setAdvancedSetting({
            ...advancedSetting,
            preSettings: newPreSettings,
          });
        }
      },
    });
  };

  const handleToggleDefault = (presetId) => {
    const updatedPresets = advancedSetting.preSettings.map((preset) => ({
      ...preset,
      isDefault: preset.id === presetId ? !preset.isDefault : false,
    }));

    setAdvancedSetting({
      ...advancedSetting,
      preSettings: updatedPresets,
    });
  };

  const handleSavePreset = (preset) => {
    let newPreSettings;
    if (isEditMode) {
      // Update existing preset
      newPreSettings = advancedSetting.preSettings.map((p) => (p.id === preset.id ? preset : p));
    } else {
      // Add new preset
      newPreSettings = [...(advancedSetting?.preSettings || []), preset];
    }

    setAdvancedSetting({
      ...advancedSetting,
      preSettings: newPreSettings,
    });
    setIsModalVisible(false);
    setEditingPreset(null);
  };

  const getTotalQuestions = (difficultyConfig) =>
    difficultyConfig?.reduce((sum, config) => sum + (config?.quantity || 0), 0);

  const getTotalDuration = (difficultyConfig) =>
    difficultyConfig?.reduce(
      (sum, config) => sum + (config?.quantity || 0) * (config?.duration || 0),
      0
    );

  const handleImportConfigs = (importedConfigs) => {
    // Generate IDs for imported configurations
    const configurationsWithIds = importedConfigs.map((config, index) => ({
      ...config,
      id: Date.now() + index, // Unique ID for each preset
    }));

    // Merge with existing presets
    const newPreSettings = [...(advancedSetting?.preSettings || []), ...configurationsWithIds];

    setAdvancedSetting({
      ...advancedSetting,
      preSettings: newPreSettings,
    });

    setIsImportModalVisible(false);
  };

  return (
    <div className="my-4 p-4 bg-light rounded">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center">
            <Text strong className="mr-2">
              {t('TESTGROUP_ADVANCED_ENABLE_TITLE')}
            </Text>
          </div>

          <div>
            <GenericToggle
              value={!isSimpleMode}
              defaultActive={!isSimpleMode}
              actionFunction={() => {
                handleToggleAdvanced(!advancedSetting?.available);
              }}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {isSimpleMode && (
        <div>
          <div className="my-2 my-sm-0 mr-0 mr-sm-2 pr-md-5 mr-md-5 mx-4 d-flex align-items-center w-60">
            <span className=" font-weight-600 text-muted font-italic text-xs pr-0 text-nowrap">
              {t('CREATE_TEST_GROUP_QUANTITY_TESTS')}
            </span>
            <input
              className="ml-2 border-thin-blue-principal font-weight-bold  py-1 max-char-input-create-testgroup px-2 outline-none h-25 col-1"
              type="number"
              onKeyDown={(e) => {
                const excludedKeys = ['e', '+', '-', '.'];
                if (excludedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              value={testGroupQuantityQuestions ?? ''}
              name="weight"
              onChange={(e) => {
                setTestGroupQuantityQuestions(e.target.value);
              }}
              onBlur={(e) => {
                onBlurInputNumber(e);
              }}
              disabled={readOnly}
            />
            <DropDownSelect
              selectedPlaceholder
              className="mx-3 py-0"
              placeholder={t('MULTIPLE_SELECT_DIFFICULTY_PLACEHOLDER')}
              setState={handleSelectDifficulty}
              selected={testGroupDifficult}
              disabled={readOnly}
              options={testDifficulty
                ?.sort((a, b) => a.order - b.order)
                ?.map((item) => ({
                  id: item.id,
                  name: t(`CREATE_TEST_GROUP_QUESTION_${item.name}`),
                  key: item.id,
                }))}
            />
            {withThemes && (
              <DropDownSelect
                selectedPlaceholder
                className="mx-3 py-0"
                placeholder={t('ABM_THEME_INPUT_PLACEHOLDER')}
                disabled={!availableThemes?.length || readOnly}
                setState={handleSelectTheme}
                selected={testGroupTheme}
                options={availableThemes?.map((item) => ({
                  id: item,
                  name: item,
                  key: item?.id ?? item,
                }))}
              />
            )}
          </div>

          {/* Subskills selector for current configuration */}
          {withThemes && (
            <div className="my-2 my-sm-0 mr-0 mr-sm-2 pr-md-5 mr-md-5 mx-4 d-flex align-items-center w-60">
              <label className="font-weight-600 text-muted font-italic text-xs pr-2 text-nowrap">
                {t('SELECT_SUBSKILLS')}:
              </label>
              <TreeMultiSelect
                options={availableSubskills}
                value={testGroupSubskills}
                onChange={(selectedIds) => {
                  if (handleSelectSubskills) {
                    handleSelectSubskills(selectedIds);
                  }
                }}
                label={t('SELECT_SUBSKILLS')}
                labelName="name"
                width="60%"
                dropdownWidth={500}
                showSelectUnselectAll
                disabled={readOnly}
                size="small"
              />
            </div>
          )}

          <div className="my-2 my-sm-0 mr-0 mr-sm-2 pr-md-5 mr-md-5 mx-4 d-flex align-items-center w-60">
            <div
              data-testid="difficulty-config-with-audio-container"
              className="d-flex align-items-center mr-4"
            >
              <Checkbox
                testid="difficulty-config-with-audio-checkbox"
                checked={testGroupListeningTest}
                onClick={handleAudioDifficulty}
                disabled={readOnly}
                className="test-checkbox align-self-start d-flex align-items-center justify-content-between"
              />
              <span
                data-testid="difficulty-config-with-audio-checkbox-text"
                className=" text-nowrap ml-2 font-weight-500 text-xs"
              >
                {t('TESTGROUP_ABM_QUESTION_WITH_AUDIO')}
              </span>
            </div>
            <button
              onClick={handleCreateQuestionsSet}
              disabled={disabledCreateSet || readOnly}
              className={`px-4 py-1 rounded-xs  text-white text-xs font-weight-bold border-0 outline-none ${
                disabledCreateSet ? 'disabled-button' : 'bg-maya-blue'
              }`}
              type="button"
            >
              {t('CREATE_TEST_GROUP_CREATE_BUTTON')}
            </button>
          </div>
          <div className="my-3">
            {testGroupQuestions
              ?.sort((a, b) => a.order - b.order)
              ?.filter((item) => (withThemes ? item.theme : !item.theme))
              ?.map((item) => (
                <TestGroupsItem
                  key={item.ordererId}
                  disabled={readOnly}
                  test={{
                    text: `${item.quantity} ${
                      Number(item.quantity) === 1
                        ? t('CREATE_TEST_GROUP_QUESTION')
                        : t('CREATE_TEST_GROUP_QUESTIONS')
                    }`,
                    questionDifficulty: (() => {
                      const difficulty = testDifficulty.find((d) => d.id === item.difficultyId);
                      return difficulty
                        ? t(`CREATE_TEST_GROUP_QUESTION_${difficulty.name}`)
                        : t('EDIT_TG_PENDING_QUESTION_DIFFICULTY');
                    })(),
                    order: item?.order,
                    theme: item?.theme,
                    listeningTest: item?.listeningTest,
                    langDisplayName: item?.langDisplayName,
                  }}
                  handleDelete={() => {
                    setShow(true);
                    setItemDelete({
                      text: `${item.quantity} ${
                        Number(item.quantity) === 1
                          ? t('CREATE_TEST_GROUP_QUESTION')
                          : t('CREATE_TEST_GROUP_QUESTIONS')
                      }`,
                      id: item.ordererId,
                      isDifficulty: true,
                    });
                  }}
                  handleOrder={handleOrderSetQuestions}
                  disableOrderUp={item?.order === 0}
                  disableOrderDown={item?.order === testGroupQuestions.length - 1}
                  testGroupABM
                />
              ))}
          </div>
        </div>
      )}
      {!isSimpleMode && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Title level={5}>{t('TESTGROUP_ADVANCED_PRESETS_TITLE')}</Title>
            {!readOnly && (
              <Space>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setIsImportModalVisible(true)}
                  size="medium"
                  className="rounded-0"
                >
                  {t('TESTGROUP_ADVANCED_IMPORT_CSV')}
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddPreset}
                  size="medium"
                  className="rounded-0"
                >
                  {t('TESTGROUP_ADVANCED_ADD_PRESET')}
                </Button>
              </Space>
            )}
          </div>

          <div className="row">
            {advancedSetting?.preSettings?.map((preset) => (
              <div key={preset.id} className="col-12 col-md-6 col-lg-4 mb-3">
                <Card
                  className={`h-100 border ${preset.isDefault ? 'border-warning' : ''}`}
                  hoverable
                  title={
                    <Space>
                      {getTranslatedValue(preset.displayName, langKey) ||
                        preset.displayName ||
                        t('TESTGROUP_ADVANCED_UNNAMED_PRESET')}
                    </Space>
                  }
                  extra={
                    !readOnly && (
                      <Space>
                        <Tooltip
                          title={
                            preset.isDefault
                              ? t('TESTGROUP_ADVANCED_UNSET_DEFAULT')
                              : t('TESTGROUP_ADVANCED_SET_DEFAULT')
                          }
                        >
                          <Button
                            icon={preset.isDefault ? <StarFilled /> : <StarOutlined />}
                            onClick={() => handleToggleDefault(preset.id)}
                            size="small"
                            className={`rounded-0 ${preset.isDefault ? 'text-warning' : ''}`}
                          />
                        </Tooltip>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => handleEditPreset(preset)}
                          size="small"
                          className="rounded-0"
                        />
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeletePreset(preset.id)}
                          size="small"
                          danger
                          className="rounded-0"
                        />
                      </Space>
                    )
                  }
                >
                  <div>
                    <div className="d-flex justify-content-between mb-2">
                      <Text type="secondary">{t('TESTGROUP_ADVANCED_TOTAL_QUESTIONS')}:</Text>
                      <Text strong>{getTotalQuestions(preset.difficultyConfig || [])}</Text>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <Text type="secondary">{t('TESTGROUP_ADVANCED_TOTAL_DURATION')}:</Text>
                      <Text strong>{getTotalDuration(preset.difficultyConfig || [])} min</Text>
                    </div>
                    <div className="mt-3">
                      {(preset.difficultyConfig || []).map((config, index) => (
                        <Tag key={index} color="blue">
                          {getTranslatedValue(config.displayName, langKey) || config.displayName}:{' '}
                          {config.quantity}
                          {config.subSkillIds && config.subSkillIds.length > 0 && (
                            <span className="ml-1">
                              ({config.subSkillIds.length} {t('SUBSKILLS').toLowerCase()})
                            </span>
                          )}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {(!advancedSetting?.preSettings || advancedSetting.preSettings.length === 0) && (
            <div className="text-center py-5 bg-white border border-dashed rounded">
              <Text type="secondary">{t('TESTGROUP_ADVANCED_NO_PRESETS')}</Text>
            </div>
          )}
        </div>
      )}

      <Modal
        title={
          isEditMode ? t('TESTGROUP_ADVANCED_EDIT_PRESET') : t('TESTGROUP_ADVANCED_CREATE_PRESET')
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingPreset(null);
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: '80rem', top: 20 }}
        bodyStyle={{ padding: '16px' }}
        centered
        destroyOnClose
      >
        {editingPreset && (
          <PresetEditor
            key={editingPreset.id}
            preset={editingPreset}
            testDifficulty={testDifficulty}
            testQuestions={testQuestions}
            withThemes={withThemes}
            availableSubskills={availableSubskills}
            getAvailableQuestionsCount={getAvailableQuestionsCount}
            onSave={handleSavePreset}
            onCancel={() => {
              setIsModalVisible(false);
              setEditingPreset(null);
            }}
          />
        )}
      </Modal>
      <ImportAdvancedConfigModal
        open={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onImport={handleImportConfigs}
        testDifficulty={testDifficulty}
        availableThemes={availableThemes}
        existingPresets={advancedSetting?.preSettings || []}
        availableSubskills={availableSubskills}
      />
    </div>
  );
};
